import {
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFundList } from "gen/endpoints/fund-entities/fund-entities";
import { useFundFamilyList } from "gen/endpoints/fund-family/fund-family";
import { useInvestorList } from "gen/endpoints/investor/investor";
import { useReportingRunCreate } from "gen/endpoints/reports/reports";
import { FundEntityReadData } from "gen/model/fundEntityReadData";
import { FundFamilyData } from "gen/model/fundFamilyData";
import { InvestorReadData } from "gen/model/investorReadData";
import { ReportingRunCreateDataRequest } from "gen/model/reportingRunCreateDataRequest";
import { ReportingRunReadData } from "gen/model/reportingRunReadData";
import { ReportTypeEnum } from "gen/model/reportTypeEnum";
import { reportingRunCreateBody } from "gen/schemas/reports/reports";
import { useEffect } from "react";
import { useForm, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { FormCheckbox } from "shared/components/input/checkbox/Checkbox";
import { FormDateInput } from "shared/components/input/date/DateInput";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { useBoolean } from "usehooks-ts";

interface AvailableReportsModalProps {
  onCloseComplete(): void;

  reportType: string;
  onSuccess: (data: ReportingRunReadData) => void;
}

interface FormInputProps {
  reportType: string;
}

export const AvailableReportsModal: React.FC<AvailableReportsModalProps> = ({
  onCloseComplete,
  reportType,
  onSuccess,
}) => {
  const { t } = useTranslation(["common", "reports"]);
  const { value: isOpen, setFalse: close } = useBoolean(true);

  const form = useForm<ReportingRunCreateDataRequest>({
    resolver: zodResolver(reportingRunCreateBody),
    defaultValues: {
      type: getReportTypeEnum(reportType),
      parameters: {
        fund_family_ids: [],
        fund_ids: [],
        investor_ids: [],
        event_ids: [],
        start_date: null,
        end_date: null,
        effective_date: null,
        include_pending: false,
        include_draft: false,
        include_cancelled: false,
      },
    },
  });

  const fundFamilyIds = form.watch("parameters.fund_family_ids");
  useEffect(() => {
    form.setValue("parameters.fund_ids", []);
  }, [fundFamilyIds, form]);

  const { mutateAsync: createReportingRun } = useReportingRunCreate();

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      onCloseComplete={onCloseComplete}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <Form
          form={form}
          onSubmit={(data) => createReportingRun({ data })}
          onSuccess={(data) => {
            onSuccess(data);
            close();
          }}
        >
          <ModalHeader>
            {t("reports:reportModal.availableModal.title", { reportType })}
          </ModalHeader>
          <ModalBody>
            <QueryBoundary>
              <FormInputs reportType={getReportTypeEnum(reportType)} />
            </QueryBoundary>
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={close} />}
              confirm={
                <FormSubmitButton>
                  {t("reports:reportModal.runReport")}
                </FormSubmitButton>
              }
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};

const FormInputs: React.FC<FormInputProps> = ({ reportType }) => {
  const { t } = useTranslation(["reports", "common"]);
  const isWaterfallTxReport =
    reportType === ReportTypeEnum.waterfall_transactions ||
    reportType === ReportTypeEnum.hypothetical_waterfall_transactions;
  return (
    <>
      <FundFamilyMultiSelect />
      <FundEntitiesMultiSelect />
      <InvestorFormMultiSelect />
      <FormDateInput
        title={t("common:optional", { field_name: t("common:startDate") })}
        name="parameters.start_date"
        controlProps={{
          pb: "16px",
        }}
      />
      <FormDateInput
        title={t("common:optional", { field_name: t("common:endDate") })}
        name="parameters.end_date"
        controlProps={{
          pb: "16px",
        }}
      />
      <FormDateInput
        title={t("common:optional", { field_name: t("common:effectiveDate") })}
        name="parameters.effective_date"
      />
      <Flex flexDirection="column" gap={1}>
        {!isWaterfallTxReport && (
          <>
            <FormCheckbox
              name="parameters.include_pending"
              defaultValue={false}
            >
              {t("reports:reportModal.includePending")}
            </FormCheckbox>
            <FormCheckbox
              name="parameters.include_cancelled"
              defaultValue={false}
            >
              {t("reports:reportModal.includeCancelled")}
            </FormCheckbox>
          </>
        )}
        <FormCheckbox name="parameters.include_draft" defaultValue={false}>
          {t("reports:reportModal.includeDraft")}
        </FormCheckbox>
      </Flex>
    </>
  );
};

export const FundFamilyMultiSelect: React.FC = () => {
  const { t } = useTranslation(["common"]);
  const { data: families } = useFundFamilyList(undefined);

  return (
    <FormSelect
      isMulti={true}
      title={t("common:fundFamily")}
      name="parameters.fund_family_ids"
    >
      {families?.map((family: FundFamilyData) => (
        <SelectOption key={family.id} value={family.id!}>
          {family.name}
        </SelectOption>
      ))}
    </FormSelect>
  );
};

export const FundEntitiesMultiSelect: React.FC = () => {
  const { t } = useTranslation(["common"]);
  const fundFamilyIds = useWatch({
    control: useFormContext().control,
    name: "parameters.fund_family_ids",
  }) as Array<string> | undefined;

  const { data: funds } = useFundList({
    fund_family_ids:
      (fundFamilyIds || []).length > 0 ? fundFamilyIds : undefined,
  });

  return (
    <FormSelect
      isMulti={true}
      title={t("common:fundEntity")}
      name="parameters.fund_ids"
    >
      {funds?.map((entity: FundEntityReadData) => (
        <SelectOption key={entity.id} value={entity.id || ""}>
          {entity.legal_name}
        </SelectOption>
      ))}
    </FormSelect>
  );
};

export const InvestorFormMultiSelect: React.FC = () => {
  const { t } = useTranslation(["common"]);
  const { data: investors } = useInvestorList(undefined);

  return (
    <FormSelect
      isMulti={true}
      title={t("common:optional", { field_name: t("common:investors") })}
      name="parameters.investor_ids"
    >
      {investors?.map((investor: InvestorReadData) => (
        <SelectOption key={investor.id} value={investor.id}>
          {investor.name}
        </SelectOption>
      ))}
    </FormSelect>
  );
};

// TODO: Replace w/ row metadata https://maybern.atlassian.net/browse/ENG-5710
const getReportTypeEnum = (reportType: string): ReportTypeEnum => {
  switch (reportType) {
    case "Capital Transactions":
      return ReportTypeEnum.capital_transactions;
    case "Transactions":
      return ReportTypeEnum.transactions;
    case "Management Fees":
      return ReportTypeEnum.management_fees;
    case "Cash Waterfall Transactions":
      return ReportTypeEnum.waterfall_transactions;
    case "Hypothetical Waterfall Transactions":
      return ReportTypeEnum.hypothetical_waterfall_transactions;
    default:
      return ReportTypeEnum.transactions;
  }
};
